import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

type MassTuple = [number, string];
type RawMass = (number | string)[];

interface SubLevel {
  masses: RawMass[];
  targets: RawMass[];
  rightDrop: boolean;
  message?: string;
}

function toMassTuple(m: RawMass): MassTuple {
  return [Number(m[0]), String(m[1])];
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: SubLevel[];
}

interface LevelsFile {
  levels: Level[];
}

type Side = 'left' | 'right' | null;

interface MassItem {
  idx: number;
  value: number;
  label: string;
  side: Side;
}

const LEVELS = (levelsData as LevelsFile).levels;

export function mountScalesboard(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  let subIndex = 0;
  let cfg: SubLevel = level.data[0]!;
  let masses: MassItem[] = [];
  let targets: MassTuple[] = [];
  void cfg;
  let correctCount = 0;
  const totalSubs = level.data.length;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Balança</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage scales">
      <div class="feedback" aria-live="polite"></div>
      <p class="scales__progress" aria-live="polite"></p>
      <p class="scales__hint"></p>
      <div class="scales__balance-wrap"></div>
      <div class="scales__totals" aria-live="polite"></div>
      <div class="scales__pool" role="list"></div>
      <div class="scales__actions">
        <button class="btn btn--secondary scales__reset" type="button">↺ Reiniciar</button>
        <button class="btn btn--primary scales__ok" type="button">Confirmar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.scales__progress')!;
  const hintEl = page.querySelector<HTMLElement>('.scales__hint')!;
  const balanceWrap = page.querySelector<HTMLElement>('.scales__balance-wrap')!;
  const totalsEl = page.querySelector<HTMLElement>('.scales__totals')!;
  const poolEl = page.querySelector<HTMLElement>('.scales__pool')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.scales__ok')!;
  const resetBtn = page.querySelector<HTMLButtonElement>('.scales__reset')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  resetBtn.addEventListener('click', () => {
    masses.forEach((m) => { m.side = null; });
    ctx.audio.play('click');
    renderAll();
  });

  function startRound(): void {
    cfg = level.data[subIndex]!;
    targets = cfg.targets.map(toMassTuple);
    masses = cfg.masses.map((m, i) => {
      const t = toMassTuple(m);
      return { idx: i, value: t[0], label: t[1], side: null };
    });
    renderProgress();
    renderHint();
    renderAll();
  }

  function totals(): { left: number; right: number } {
    const left = masses.filter((m) => m.side === 'left').reduce((a, b) => a + b.value, 0);
    const right = targets.reduce((a, b) => a + b[0], 0) +
      masses.filter((m) => m.side === 'right').reduce((a, b) => a + b.value, 0);
    return { left, right };
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${subIndex + 1} de ${totalSubs}`;
  }

  function renderHint(): void {
    const base = cfg.rightDrop
      ? 'Use os dois lados pra equilibrar a balança.'
      : 'Coloque pesos na esquerda pra equilibrar.';
    hintEl.textContent = `${base} Toque num peso na balança pra devolver.`;
  }

  function renderAll(): void {
    renderBalance();
    renderTotals();
    renderPool();
  }

  function renderBalance(): void {
    const { left, right } = totals();
    balanceWrap.innerHTML = '';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 480 240');
    svg.setAttribute('class', 'scales__balance');

    // Calcula rotação: max ±15°. Diferenças pequenas → ângulo pequeno.
    const diff = right - left;
    const cap = Math.max(8, Math.abs(diff));
    const angle = Math.max(-15, Math.min(15, (diff / cap) * 15));

    // Base
    const base = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    base.setAttribute('d', 'M 200 210 L 280 210 L 270 220 L 210 220 Z');
    base.setAttribute('class', 'scales__base');
    svg.appendChild(base);
    // Pilar
    const pilar = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    pilar.setAttribute('x', '236');
    pilar.setAttribute('y', '90');
    pilar.setAttribute('width', '8');
    pilar.setAttribute('height', '120');
    pilar.setAttribute('class', 'scales__pilar');
    svg.appendChild(pilar);

    // Barra (rotacionada)
    const bar = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    bar.setAttribute('transform', `rotate(${angle} 240 90)`);
    bar.setAttribute('class', 'scales__bar-group');
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', '60');
    line.setAttribute('x2', '420');
    line.setAttribute('y1', '90');
    line.setAttribute('y2', '90');
    line.setAttribute('class', 'scales__bar');
    bar.appendChild(line);
    // Pino central
    const pivot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    pivot.setAttribute('cx', '240');
    pivot.setAttribute('cy', '90');
    pivot.setAttribute('r', '6');
    pivot.setAttribute('class', 'scales__pivot');
    bar.appendChild(pivot);

    // Pratos
    const leftItems = masses.filter((m) => m.side === 'left').map((m) => ({ value: m.value, label: m.label, removable: true, idx: m.idx }));
    const rightItems = mergePlatterItems(targets, masses.filter((m) => m.side === 'right'));
    const platterLeft = makePlatter(110, 90, 'left', leftItems);
    const platterRight = makePlatter(370, 90, 'right', rightItems);
    bar.appendChild(platterLeft);
    bar.appendChild(platterRight);

    svg.appendChild(bar);
    balanceWrap.appendChild(svg);
  }

  function makePlatter(cx: number, cy: number, side: 'left' | 'right', items: { value: number; label: string; removable?: boolean; idx?: number }[]): SVGElement {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('class', `scales__platter scales__platter--${side}`);
    // Corda
    const rope = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    rope.setAttribute('x1', String(cx));
    rope.setAttribute('x2', String(cx));
    rope.setAttribute('y1', String(cy));
    rope.setAttribute('y2', String(cy + 32));
    rope.setAttribute('class', 'scales__rope');
    g.appendChild(rope);
    // Prato
    const platter = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
    platter.setAttribute('cx', String(cx));
    platter.setAttribute('cy', String(cy + 36));
    platter.setAttribute('rx', '50');
    platter.setAttribute('ry', '8');
    platter.setAttribute('class', 'scales__platter-base');
    g.appendChild(platter);

    // Items empilhados
    items.forEach((item, i) => {
      const w = 24;
      const h = 16;
      const totalW = items.length * w;
      const x = cx - totalW / 2 + i * w + w / 2;
      const y = cy + 36 - h - 1;
      const chip = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      chip.setAttribute('x', String(x - w / 2));
      chip.setAttribute('y', String(y));
      chip.setAttribute('width', String(w));
      chip.setAttribute('height', String(h));
      chip.setAttribute('rx', '3');
      chip.setAttribute('class', `scales__chip ${item.removable ? 'scales__chip--removable' : 'scales__chip--fixed'}`);
      g.appendChild(chip);
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(x));
      text.setAttribute('y', String(y + h - 4));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('class', 'scales__chip-text');
      text.textContent = item.label;
      g.appendChild(text);
      if (item.removable && item.idx !== undefined) {
        const idx = item.idx;
        chip.setAttribute('data-idx', String(idx));
        chip.addEventListener('click', () => {
          const m = masses[idx]!;
          m.side = null;
          ctx.audio.play('click');
          renderAll();
        });
      }
    });

    return g;
  }

  function mergePlatterItems(targetsList: MassTuple[], extras: MassItem[]): { value: number; label: string; removable?: boolean; idx?: number }[] {
    const fixed = targetsList.map((t) => ({ value: t[0], label: t[1], removable: false }));
    const removable = extras.map((m) => ({ value: m.value, label: m.label, removable: true, idx: m.idx }));
    return [...fixed, ...removable];
  }

  function renderTotals(): void {
    const { left, right } = totals();
    const cls = left === right ? 'scales__totals--match' : '';
    totalsEl.innerHTML = `<span class="scales__totals-side ${cls}">Esquerda: <strong>${left}</strong></span><span class="scales__totals-side ${cls}">Direita: <strong>${right}</strong></span>`;
  }

  function renderPool(): void {
    poolEl.innerHTML = '';
    masses.forEach((m) => {
      if (m.side !== null) return;
      const wrap = document.createElement('div');
      wrap.className = 'scales__pool-item';
      wrap.innerHTML = `
        <div class="scales__weight">${m.label}</div>
        <div class="scales__weight-actions">
          <button type="button" class="scales__weight-btn scales__weight-btn--left" aria-label="Mover ${m.label} pra esquerda" data-idx="${m.idx}" data-side="left">⟵</button>
          ${cfg.rightDrop ? `<button type="button" class="scales__weight-btn scales__weight-btn--right" aria-label="Mover ${m.label} pra direita" data-idx="${m.idx}" data-side="right">⟶</button>` : ''}
        </div>
      `;
      poolEl.appendChild(wrap);
    });
    poolEl.querySelectorAll<HTMLButtonElement>('.scales__weight-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.idx ?? '0', 10);
        const side = btn.dataset.side as 'left' | 'right';
        const m = masses[idx];
        if (!m || m.side !== null) return;
        m.side = side;
        ctx.audio.play('click');
        renderAll();
      });
    });
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    const { left, right } = totals();
    if (left === right && (left > 0 || right > 0)) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      subIndex++;
      window.setTimeout(() => {
        if (subIndex < totalSubs) startRound();
        else finishLevel();
      }, 800);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
    }
  }

  okBtn.addEventListener('click', validate);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      validate();
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você equilibrou ${correctCount} de ${totalSubs} balanças.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/scalesboard/${level.level + 1}`;
      });
    }
  }

  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}
